// areaFill.js — Canvas drawing primitives for tactical area fill variants.
//
// Three public draw functions, one centroid-glyph dispatcher:
//
//   drawSolidArea(ctx, ring, style)
//   drawHatchArea(ctx, ring, color, spacing?)
//   drawPerimeterStampArea(ctx, ring, opts)
//   drawCentroidGlyph(ctx, center, glyph, size, color)
//
// Pure geometry exports (also used by tests):
//   shoelaceCentroid(ring) → [cx, cy]
//   hexToRgba(hex, alpha)  → 'rgba(...)'

// ── Geometry helpers ─────────────────────────────────────────────────────────

/**
 * Compute the visual centroid of a closed polygon ring using the shoelace formula.
 *
 * @param {[number,number][]} ring  Closed ring (last point === first point)
 * @returns {[number, number]}
 */
export function shoelaceCentroid(ring) {
    let area = 0, cx = 0, cy = 0;
    const n = ring.length - 1;
    for (let i = 0; i < n; i++) {
        const [x0, y0] = ring[i];
        const [x1, y1] = ring[i + 1];
        const cross = x0 * y1 - x1 * y0;
        area += cross;
        cx += (x0 + x1) * cross;
        cy += (y0 + y1) * cross;
    }
    area /= 2;
    if (area === 0) {
        // Degenerate polygon — fall back to bounding-box centre
        const xs = ring.map((p) => p[0]);
        const ys = ring.map((p) => p[1]);
        return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
    }
    cx /= 6 * area;
    cy /= 6 * area;
    return [cx, cy];
}

/**
 * @param {string} hex  '#rrggbb' — or any CSS color string (passed through unchanged)
 * @param {number} alpha  0–1
 * @returns {string}  'rgba(r,g,b,a)'
 */
export function hexToRgba(hex, alpha) {
    if (!hex || !hex.startsWith('#') || hex.length !== 7) {
        // Pass-through for rgba/hsla/named colors; caller gets the color as-is.
        return hex ?? 'rgba(0,0,0,0)';
    }
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

// ── Path helpers ─────────────────────────────────────────────────────────────

function tracePath(ctx, ring) {
    ring.forEach((p, i) => (i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1])));
}

// ── Variant A — Solid / Transparent Fill ─────────────────────────────────────

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {[number,number][]} ring   Closed screen-space ring
 * @param {{ color: string, fillOpacity?: number, lineWidth?: number, dash?: number[] }} style
 */
export function drawSolidArea(ctx, ring, style) {
    const { color, fillOpacity = 0.12, lineWidth = 2, dash = [] } = style;
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, ring);
    ctx.closePath();
    ctx.fillStyle = hexToRgba(color, fillOpacity);
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash(dash);
    ctx.stroke();
    ctx.restore();
}

// ── Variant B — Diagonal Hatch Fill (CBRN) ───────────────────────────────────

/**
 * Fill a polygon with a 45° diagonal hatch pattern, draw the outline on top.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {[number,number][]} ring    Closed screen-space ring
 * @param {string}            color
 * @param {number}            [spacing=8]  px between hatch lines
 */
export function drawHatchArea(ctx, ring, color, spacing = 8) {
    // 1. Build a seamless repeating tile.
    //    Three line segments ensure full coverage at tile edges without seams.
    const tile = document.createElement('canvas');
    tile.width = tile.height = spacing;
    const t = tile.getContext('2d');
    t.strokeStyle = color;
    t.lineWidth = 1;
    t.beginPath();
    t.moveTo(0, spacing);        t.lineTo(spacing, 0);                          // primary diagonal
    t.moveTo(-spacing / 2, spacing / 2); t.lineTo(spacing / 2, -spacing / 2);  // wrap-around left
    t.moveTo(spacing / 2, spacing * 1.5); t.lineTo(spacing * 1.5, spacing / 2); // wrap-around right
    t.stroke();

    const pattern = ctx.createPattern(tile, 'repeat');

    // 2. Clip to polygon, fill, restore clip
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, ring);
    ctx.closePath();
    ctx.clip();

    ctx.fillStyle = pattern;
    const xs = ring.map((p) => p[0]);
    const ys = ring.map((p) => p[1]);
    ctx.fillRect(
        Math.min(...xs), Math.min(...ys),
        Math.max(...xs) - Math.min(...xs),
        Math.max(...ys) - Math.min(...ys),
    );
    ctx.restore(); // removes clip

    // 3. Outline on top of fill
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, ring);
    ctx.closePath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.stroke();
    ctx.restore();
}

// ── Centroid Glyphs ──────────────────────────────────────────────────────────

/**
 * Draw the appropriate CBRN centroid glyph.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {[number,number]} center   [cx, cy] in screen space
 * @param {'mushroom'|'inverted-t'|'teardrop'|'trefoil'} glyph
 * @param {number}  [size=18]   Nominal glyph height in px
 * @param {string}  color
 */
export function drawCentroidGlyph(ctx, center, glyph, size = 18, color) {
    const [cx, cy] = center;
    const r = size / 2;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 1.5;

    switch (glyph) {
        case 'mushroom':
            _drawMushroom(ctx, cx, cy, r);
            break;
        case 'inverted-t':
            _drawInvertedT(ctx, cx, cy, r);
            break;
        case 'teardrop':
            _drawTeardrop(ctx, cx, cy, r);
            break;
        case 'trefoil':
            _drawTrefoil(ctx, cx, cy, r);
            break;
        default:
            // Unknown glyph — draw nothing
            break;
    }
    ctx.restore();
}

// Nuclear — vertical stem + domed cap
function _drawMushroom(ctx, cx, cy, r) {
    const stemTop = cy;
    const stemBot = cy + r;
    ctx.beginPath();
    ctx.moveTo(cx, stemTop);
    ctx.lineTo(cx, stemBot);
    ctx.stroke();
    // dome (semi-circle above stem top)
    ctx.beginPath();
    ctx.arc(cx, stemTop, r * 0.7, Math.PI, 0);
    ctx.stroke();
}

// Chemical — vertical stem + horizontal crossbar at bottom
function _drawInvertedT(ctx, cx, cy, r) {
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx, cy + r * 0.3); // stem
    ctx.moveTo(cx - r, cy + r * 0.3);
    ctx.lineTo(cx + r, cy + r * 0.3); // crossbar
    ctx.stroke();
}

// Biological — filled teardrop pointing downward
function _drawTeardrop(ctx, cx, cy, r) {
    ctx.beginPath();
    // Teardrop: circle + pointed bottom
    const topY = cy - r * 0.4;
    ctx.arc(cx, topY, r * 0.55, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.4, topY + r * 0.3);
    ctx.quadraticCurveTo(cx, cy + r, cx + r * 0.4, topY + r * 0.3);
    ctx.closePath();
    ctx.fill();
}

// Radiological — 3-lobe radiation trefoil
function _drawTrefoil(ctx, cx, cy, r) {
    const innerR = r * 0.2;
    const lobeR  = r * 0.4;
    // Three lobes at 0°, 120°, 240°
    for (let i = 0; i < 3; i++) {
        const angle = (i * 2 * Math.PI) / 3 - Math.PI / 2;
        const lx = cx + (innerR + lobeR) * Math.cos(angle);
        const ly = cy + (innerR + lobeR) * Math.sin(angle);
        ctx.beginPath();
        ctx.arc(lx, ly, lobeR, 0, Math.PI * 2);
        ctx.fill();
    }
    // Inner clear circle
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

// ── Variant C — Perimeter Stamp Walk ─────────────────────────────────────────

/**
 * Draw a polygon outline then stamp a glyph along the perimeter at fixed stride.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {[number,number][]} ring  Closed screen-space ring
 * @param {{
 *   color:  string,
 *   stride: number,
 *   stamp:  'tick-out'|'tick-in'|'tick-alt-circle'|'circles-at-centroid'|'none',
 *   lineWidth?: number,
 * }} opts
 */
export function drawPerimeterStampArea(ctx, ring, opts) {
    const { color, stride, stamp, lineWidth = 2 } = opts;

    // 1. Draw the outline
    ctx.save();
    ctx.beginPath();
    tracePath(ctx, ring);
    ctx.closePath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    ctx.stroke();
    ctx.restore();

    if (stamp === 'none') return;

    if (stamp === 'circles-at-centroid') {
        _drawCentroidCircles(ctx, ring, color);
        return;
    }

    // 2. Walk the closed perimeter
    const closedRing = ring[ring.length - 1][0] === ring[0][0] && ring[ring.length - 1][1] === ring[0][1]
        ? ring
        : [...ring, ring[0]];

    _perimeterWalk(ctx, closedRing, stride, stamp, color);
}

// Walk the perimeter at `stride` px intervals, stamping at each position.
function _perimeterWalk(ctx, ring, stride, stamp, color) {
    let accumulated = stride / 2; // Start half a stride in so stamps don't cluster at vertex 0
    let alternate = false;

    for (let i = 0; i < ring.length - 1; i++) {
        const [x0, y0] = ring[i];
        const [x1, y1] = ring[i + 1];
        const dx = x1 - x0;
        const dy = y1 - y0;
        const segLen = Math.sqrt(dx * dx + dy * dy);
        if (segLen === 0) continue;

        const ux = dx / segLen; // unit vector along segment
        const uy = dy / segLen;
        // Outward normal for a CCW ring: right-hand perpendicular of (ux,uy) = (uy,-ux)
        const nx = uy;
        const ny = -ux;

        let dist = accumulated;
        while (dist <= segLen) {
            const px = x0 + ux * dist;
            const py = y0 + uy * dist;

            if (stamp === 'tick-out') {
                _drawTick(ctx, px, py, nx, ny, 6, color);
            } else if (stamp === 'tick-in') {
                _drawTick(ctx, px, py, -nx, -ny, 6, color);
            } else if (stamp === 'tick-alt-circle') {
                if (!alternate) {
                    _drawTick(ctx, px, py, nx, ny, 6, color);
                } else {
                    _drawSmallCircle(ctx, px, py, 3, color);
                }
                alternate = !alternate;
            }

            dist += stride;
        }
        accumulated = dist - segLen;
    }
}

function _drawTick(ctx, px, py, nx, ny, len, color) {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px + nx * len, py + ny * len);
    ctx.stroke();
    ctx.restore();
}

function _drawSmallCircle(ctx, px, py, r, color) {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
}

function _drawCentroidCircles(ctx, ring, color) {
    const [cx, cy] = shoelaceCentroid(ring);
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    [5, 10].forEach((r) => {
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
    });
    ctx.restore();
}
